import React, {
  useState,
  useContext,
  useEffect,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import format from 'string-template';

import FirebaseContext from '../../../../../context/FirebaseContext';
import LoggedInUserContext from '../../../../../context/LoggedInUserContext';
import useToast from '../../../../hooks/useToast';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import DialogModal from '../../../../../components/DialogModal';
import Button from '../../../../../components/Button';
import { StyledTextField } from '../../../../../components/Inputs';
import LoadingOverlay from '../../../../components/LoadingOverlay';
import LabelCheckbox from '../../../../components/LabelCheckbox';
import { TimeDurationFormat } from '../../../../../utils/time';
import { DateFormat } from '../../../../../utils/date';

import {
  changeISAvailability,
  generateAvailabilityEvents,
} from '../../utils';
import { CAL_DOT_COM_TIME_ZONE } from '../../constants';

import {
  ModalContainer,
  ModalTitle,
  TimeSlotsContainer,
  TimeSlotRow,
  TimeSlotLabel,
  TimeSlotInput,
  ButtonContainer,
  RecurringSection,
  DaysContainer,
  RepeatDay,
  DateInputContainer,
  DateInputLabel,
} from './styles';
import texts from './texts.json';

const AvilabilityModal = ({
  showModal,
  onClose,
  selectedDate,
  maxSeatsPerSlot,
  isLoading = false,
  setIsLoading,
  setAvailabilityEventDetails,
  setShowModal,
  visibleDateRange,
  setSelectedDate,
  selectedDateAvailabilityData,
}) => {
  const [availabilityData, setAvailabilityData] = useState([]);
  const [selectedDays, setSelectedDays] = useState([]);
  const [untilDate, setUntilDate] = useState('');
  const [isRecurringDaysOptionShown, setIsRecurringDaysOptionShown] = useState(false);
  const [errors, setErrors] = useState({});

  const { showToast } = useToast();
  const isComponentMountedRef = useComponentMounted();
  const { firebase: { remote } } = useContext(FirebaseContext);
  const { userId } = useContext(LoggedInUserContext);

  useEffect(() => {
    const fetchTimeSlots = async () => {
      setIsLoading(true);
      const formattedTimeSlots = selectedDateAvailabilityData.map((slot) => ({
        id: moment(slot.start).tz(CAL_DOT_COM_TIME_ZONE).format(DateFormat.DEFAULT_DATE_WITH_TIME_FORMAT),
        time: moment(slot.start).tz(CAL_DOT_COM_TIME_ZONE),
        availability: slot.availability,
      }));
      setAvailabilityData(formattedTimeSlots);
      setUntilDate(selectedDate);
      setIsLoading(false);
    };
    if (selectedDate) {
      fetchTimeSlots();
    }
  }, [selectedDate, selectedDateAvailabilityData, setIsLoading]);

  const handleInputChange = useCallback((index, value) => {
    const numericValue = Number(value);

    if (numericValue < 0 || numericValue > maxSeatsPerSlot) {
      setErrors((prev) => ({ ...prev, [index]: format(texts.error, { maxSeatsPerSlot }) }));
    } else {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
    }
    setAvailabilityData((prev) => {
      const newAvailabilityData = [...prev];
      newAvailabilityData[index].availability = numericValue;
      return newAvailabilityData;
    });
  }, [maxSeatsPerSlot]);

  const handleDaysSelect = (day) => {
    if (selectedDays.includes(day)) {
      setSelectedDays(selectedDays.filter((selectedDay) => selectedDay !== day));
    } else {
      setSelectedDays([...selectedDays, day]);
    }
  };

  const handleSaveAvailability = useCallback(async () => {
    const hasErrors = Object.values(errors).some((error) => error);
    if (hasErrors) {
      return;
    }
    if (isRecurringDaysOptionShown && selectedDays.length === 0) {
      showToast(texts.selectDaysError, { error: true });
      return;
    }

    try {
      setIsLoading(true);
      const requestData = {
        availabilityData: availabilityData.map((availability) => ({
          time: new Date(availability.time),
          availability: availability.availability,
        })),
        userId,
      };
      // Add recurring data to the request data if the recurring option is shown
      if (isRecurringDaysOptionShown) {
        requestData.recurring = {
          days: selectedDays,
          untilDate,
        };
      }

      const { success, err } = await changeISAvailability(remote, requestData);

      if (success && isComponentMountedRef.current) {
        const newAvailabilityData = await generateAvailabilityEvents(visibleDateRange);
        setAvailabilityEventDetails(newAvailabilityData);
        showToast(texts.availabilitySaved);
      } else {
        showToast(format(texts.errorSavingAvailability, { err }), { error: true });
      }
    } catch (err) {
      showToast(format(texts.errorSavingAvailability, { err }), { error: true });
    } finally {
      if (isComponentMountedRef.current) {
        setShowModal(false);
        setIsLoading(false);
        setSelectedDays([]);
        setUntilDate('');
        setIsRecurringDaysOptionShown(false);
        setAvailabilityData([]);
        setSelectedDate(null);
        onClose();
      }
    }
  }, [
    showToast,
    remote,
    isComponentMountedRef,
    userId,
    visibleDateRange,
    availabilityData,
    selectedDays,
    untilDate,
    isRecurringDaysOptionShown,
    errors,
    onClose,
    setAvailabilityEventDetails,
    setShowModal,
    setIsLoading,
    setSelectedDate,
  ]);

  const hasErrors = Object.keys(errors).length > 0;

  return (
    <DialogModal
      open={showModal}
      onClose={onClose}
      maxWidth="sm"
    >
      <ModalContainer>
        <ModalTitle>
          {format(texts.editAvailabilityFor, {
            date: moment(selectedDate).format(DateFormat.DEFAULT_DATE_FORMAT),
            untilDate: isRecurringDaysOptionShown ? format(texts.until, {
              date: moment(untilDate).format(DateFormat.DEFAULT_DATE_FORMAT),
            }) : '',
          })}
        </ModalTitle>
        <TimeSlotsContainer>
          {availabilityData.map((availability, index) => (
            <TimeSlotRow key={availability.id}>
              <TimeSlotLabel>
                {moment(availability.time, TimeDurationFormat.CLOCK_FORMAT)
                  .format(TimeDurationFormat.TWELVE_HOUR_FORMAT)}
              </TimeSlotLabel>
              <TimeSlotInput>
                <StyledTextField
                  type="number"
                  value={availability?.availability}
                  onChange={(e) => handleInputChange(index, e.target.value)}
                  error={!!errors[index]}
                  helperText={errors[index] || ''}
                  inputProps={{
                    min: 0,
                    max: maxSeatsPerSlot,
                  }}
                  $simple
                />
              </TimeSlotInput>
            </TimeSlotRow>
          ))}
        </TimeSlotsContainer>

        <LabelCheckbox
          description={texts.repeat}
          isChecked={isRecurringDaysOptionShown}
          onChange={() => setIsRecurringDaysOptionShown((prev) => !prev)}
        />

        {isRecurringDaysOptionShown && (
          <RecurringSection>
            <DaysContainer>
              {moment.weekdaysShort().map((day) => (
                <RepeatDay
                  key={day}
                  onClick={() => handleDaysSelect(day)}
                  isSelected={selectedDays.includes(day)}
                >
                  {day}
                </RepeatDay>
              ))}
            </DaysContainer>
            <DateInputContainer>
              <DateInputLabel>{texts.untilDate}</DateInputLabel>
              <StyledTextField
                type="date"
                value={untilDate}
                onChange={(e) => setUntilDate(e.target.value)}
                inputProps={{
                  min: selectedDate,
                }}
                $simple
              />
            </DateInputContainer>
          </RecurringSection>
        )}

        <ButtonContainer>
          <Button
            variant="secondary"
            onClick={onClose}
            full={false}
          >
            {texts.cancel}
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveAvailability}
            disabled={hasErrors || isLoading}
            full={false}
          >
            {texts.saveChanges}
          </Button>
        </ButtonContainer>

        <LoadingOverlay isLoading={isLoading} />
      </ModalContainer>
    </DialogModal>
  );
};

AvilabilityModal.propTypes = {
  showModal: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedDate: PropTypes.string,
  maxSeatsPerSlot: PropTypes.number,
  setAvailabilityEventDetails: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  setIsLoading: PropTypes.func.isRequired,
  setShowModal: PropTypes.func.isRequired,
  visibleDateRange: PropTypes.object,
  setSelectedDate: PropTypes.func.isRequired,
  selectedDateAvailabilityData: PropTypes.array,
};

AvilabilityModal.defaultProps = {
  selectedDate: '',
  maxSeatsPerSlot: 5,
  isLoading: false,
  visibleDateRange: null,
  selectedDateAvailabilityData: [],
};

export default AvilabilityModal;
